import {Component, OnInit, TemplateRef} from '@angular/core';
import {BlockStatementAbstractComponent} from '../block-statement-abstract.component';
import {FinancialReward} from '../../types/financial.reward.type';

@Component({
    selector: 'core-financial-rewards',
    templateUrl: './block-financial-rewards.component.html',
    standalone: false
})
export class BlockFinancialRewardsComponent extends BlockStatementAbstractComponent implements OnInit {
    financialRewards: FinancialReward[] | any;
    isMobileStyles!: boolean;
    uniqueCatsArray: any;
    isFiguresHide: any;
    currency: any;
    contentRotateReset!: number;
    reward!: FinancialReward;

    // Print PDF logic
    usersFinancialRewardsLengthForPdf!: number;
    isPageTwoNeededForPdf!: boolean;

    // Modals
    private template!: TemplateRef<any>;
    private template2!: TemplateRef<any>;

    // Scale stuff
    minScale = 0.35;
    baseScale = 320; // Inner circle dims (300px)
    minTextScale = 0.7;
    max = 0;
    min!: number;
    scaleFactor!: number;
    useLinear!: boolean;

    // For inner (largest) circle
    innerCircleSize!: string;
    innerCircleTextScale: any;
    innerCircleValue!: string;
    innerCircleName!: string;
    innerCircleLongDescription!: string;

    /* This mapping is used to position the reward circles around the centre circle
    and caters for 2-10 rewards (max a client can have is 10).

       There are two things to note:
       1. The number of outer circles (rewards).
          - We divide 360 by this number to get the degrees to position the outer circles (all circles start in the centre and we
            rotate them out via css to the required position using degrees).
            Eg. 360 / 9 = 40. So 9 outer circles will be positioned at 40deg points around the inner circle (position 1 is always the
            centre circle so the first outer circle is position 2 and set to 0 deg (3 o'clock on clock face). Position 3 would be 40deg,
            position 4 would be 80deg etc.
       2. The rank (based off of the reward value).
          - The reward values are in high to low order which sets the ranking. Rank 2 is always the largest value/circle with higher
            rank numbers being smaller.
            Using a combination of rank and position we can prevent larger circles overlapping by positioning smaller circles either
            side of them.

       To summarise, changing these values will move reward circles to another position around the centre circle.
       So best not to mess around with these unless you know the implications.
    */
    rankAndPositionMapping = [
        {
            noOfOuterRewards: 9,
            mapping: [
                {rank: 2, position: 10},
                {rank: 3, position: 7},
                {rank: 4, position: 5},
                {rank: 5, position: 3},
                {rank: 6, position: 8},
                {rank: 7, position: 9},
                {rank: 8, position: 4},
                {rank: 9, position: 2},
                {rank: 10, position: 6}
            ]
        },
        {
            noOfOuterRewards: 8,
            mapping: [
                {rank: 2, position: 9},
                {rank: 3, position: 4},
                {rank: 4, position: 6},
                {rank: 5, position: 5},
                {rank: 6, position: 3},
                {rank: 7, position: 7},
                {rank: 8, position: 8},
                {rank: 9, position: 2}
            ]
        },
        {
            noOfOuterRewards: 7,
            mapping: [
                {rank: 2, position: 8},
                {rank: 3, position: 4},
                {rank: 4, position: 6},
                {rank: 5, position: 3},
                {rank: 6, position: 5},
                {rank: 7, position: 7},
                {rank: 8, position: 2}
            ]
        },
        {
            noOfOuterRewards: 6,
            mapping: [
                {rank: 2, position: 2},
                {rank: 3, position: 5},
                {rank: 4, position: 4},
                {rank: 5, position: 3},
                {rank: 6, position: 6},
                {rank: 7, position: 7}
            ]
        },
        {
            noOfOuterRewards: 5,
            mapping: [
                {rank: 2, position: 2},
                {rank: 3, position: 4},
                {rank: 4, position: 5},
                {rank: 5, position: 6},
                {rank: 6, position: 3}
            ]
        },
        {
            noOfOuterRewards: 4,
            mapping: [
                {rank: 2, position: 2},
                {rank: 3, position: 4},
                {rank: 4, position: 5},
                {rank: 5, position: 3}
            ]
        },
        {
            noOfOuterRewards: 3,
            mapping: [
                {rank: 2, position: 2},
                {rank: 3, position: 3},
                {rank: 4, position: 4}
            ]
        },
        {
            noOfOuterRewards: 2,
            mapping: [
                {rank: 2, position: 2},
                {rank: 3, position: 3}
            ]
        },
        {
            noOfOuterRewards: 1,
            mapping: [
                {rank: 2, position: 2},
            ]
        }
    ];

    override ngOnInit(): void {
        super.ngOnInit();
        this.financialRewards = this.sortRewards(this.trsService.getUserFinancialRewards()) || [];

        if (this.financialRewards.length > 0) {
            this.min = this.financialRewards.slice(-1)[0].value;
            this.max = this.financialRewards.slice(0)[0].value;

            if (this.financialRewards.slice(1)
                && this.financialRewards.slice(1)[0].value > this.max / 3) {
                this.max = this.max / 2.75;
            }

            this.currency = this.trsService.getCurrencyCode();
            this.usersFinancialRewardsLengthForPdf = this.trsService.getUserFinancialRewardsLengthForPdf();
            this.isPageTwoNeededForPdf = this.trsService.isFinancialRewardPageTwoNeededForPdf();

            this.subscriptions.push(this.trsService.getFiguresToggleState$().subscribe((value: boolean) => {
                this.isFiguresHide = value;
            }));

            // Styles/layout changed for mobile
            this.breakpoint
                .observe(['(max-width: 991px)'])
                .subscribe(result => {
                    this.isMobileStyles = result.matches;
                });

            if (!this.isMobileStyles) {
                // Get circle scale values only for desktop layout (using stacked buttons medium bp down)
                this.scaleFactor = this.max - this.min;
                this.useLinear = this.min / this.max >= this.minScale;
                this.getScaleValues(this.min, this.max, this.scaleFactor, this.useLinear);
            } else {
                const innerCircleReward = this.financialRewards[0];
                this.getInnerCircleContent(innerCircleReward.value, innerCircleReward.name, innerCircleReward.longDescriptionDigital);
            }
        }
    }

    sortRewards(data: FinancialReward[]) {
        return data.sort((a, b) => b.value - a.value);
    }

    // Get scale values for our circles
    getScaleValues(min: any, max: any, scaleFactor: any, useLinear: any) {
        let scale: number;
        this.financialRewards.forEach((reward: FinancialReward, index: number) => {
            const value = reward.value;
            const xval = (value - min) / scaleFactor;
            const yval = Math.sqrt(xval);
            let textScale = this.minTextScale + (yval * (1 - this.minTextScale));
            if (useLinear) {
                scale = value / max;
                textScale = Math.max(textScale, scale);
            } else {
                scale = this.minScale + (yval * (1 - this.minScale));
            }
            const scaleValue = this.baseScale * scale;

            this.addPositionAndScaleDataToObj(reward, scaleValue, textScale, index, value);
        });
    }

    // Add position & scale values to our financialRewards object for looping over in html.
    addPositionAndScaleDataToObj(reward: any, scaleValue: any, textScale: any, index: any, value: any) {
        // Add scale value & text scale to our data
        reward.size = scaleValue.toFixed(2);
        reward.textScale = textScale.toFixed(2);
        reward.rank = index + 1;

        // Get number of reward circles (minus 1 to remove the inner circle as we don't count this)
        const noOfOuterCircles = this.financialRewards.length - 1;
        const getAngleIncrement = this.getAngleIncrement(noOfOuterCircles);
        //
        // // Use predefined mapping to position reward circles based on their value rank
        for (const item of this.rankAndPositionMapping) {
            if (item.noOfOuterRewards === noOfOuterCircles) {
                item.mapping.forEach((itemValue: any) => {
                    if (itemValue.rank === reward.rank) {
                        // Adding position values to our object for *ngFor loop in html
                        reward.outerCirclePosition = itemValue.position;
                        // Translate value which actions the distance out from the center circle
                        reward.translateValue = this.getTranslateValue(reward.size);
                        // These rotate the circles to their outer positions and reverse rotate content to be horizontal.
                        reward.contentRotateReset = (getAngleIncrement * (parseInt(reward.outerCirclePosition, 10) - 2) * -1).toFixed();
                        reward.degrees = (getAngleIncrement * (parseInt(reward.outerCirclePosition, 10) - 2)).toFixed();
                    }
                });
            }
        }

        // Set variables for inner circle used outside *ngFor in html
        if (!this.innerCircleSize) {
            this.innerCircleSize = reward.size;
            this.innerCircleTextScale = reward.textScale;
            this.getInnerCircleContent(value, reward.name, reward.longDescriptionDigital);
        }
    }

    getInnerCircleContent(value: any, name: any, desc: any) {
        this.innerCircleValue = value;
        this.innerCircleName = name;
        this.innerCircleLongDescription = desc;
    }

    // Get distance the outer circles are pushed out from the centre.
    getTranslateValue(rewardSize: any) {
        // translate needs a percentage of dims of circle adding to 240px if > 150px
        // These figures could be tweaked if required.
        if (rewardSize <= 150) {
            return '240px';
        } else {
            const newTranslate = (rewardSize / 5) + 240; // Bit of trial and error so (rewardSize / 5) could change.
            return newTranslate + 'px';
        }
    }

    // Returns the angle increment of a section of the inner circle.
    // Our outer circles will be positioned in these increments (like number positions on a clock face).
    getAngleIncrement(noOfOuterCircles: any) {
        return 360 / noOfOuterCircles;
    }

    // Show abbreviated name based on some very rough string lengths.
    useFullNameOrAbbreviated(name: any, abbrName: any, scale: any) {
        if (this.isMobileStyles) {
            return name;
        } else {
            return scale < 150 && name.length > 12 ? abbrName : name;
        }
    }

    openModal(template: TemplateRef<any>) {
        this.template = template;
        this.modalRef = this.modalService!.show(this.template);
    }

    openModal2(template: TemplateRef<any>, reward: FinancialReward, evt: Event) {
        evt.stopPropagation();
        this.reward = reward;
        this.template2 = template;
        this.modalRef = this.modalService!.show(this.template2);
    }

    getFinancialTotal(): any {
        let total = 0;
        this.financialRewards.map((reward: FinancialReward) => {
            total = total + reward.value;
        });
        return total;
    }

}
